import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboardRealtime } from '../context/RealtimeContext';
import { DashboardApi } from '../services/api';
import { notifyError } from '../utils/statusShortcuts';
import './Sidebar.css';

const Icon = ({ name }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M4 11.5 12 4l8 7.5" />
          <path d="M6.5 10.5V20h11v-9.5" />
          <path d="M10 20v-5h4v5" />
        </svg>
      );
    case 'products':
      return (
        <svg {...common}>
          <rect x="4" y="5" width="7" height="7" rx="2" />
          <rect x="13" y="5" width="7" height="7" rx="2" />
          <rect x="4" y="14" width="7" height="5" rx="2" />
          <rect x="13" y="14" width="7" height="5" rx="2" />
        </svg>
      );
    case 'dashboard':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="2" />
          <rect x="14" y="3" width="7" height="4" rx="2" />
          <rect x="14" y="9" width="7" height="12" rx="2" />
          <rect x="3" y="12" width="7" height="9" rx="2" />
        </svg>
      );
    case 'billing':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="3" />
          <path d="M3 10.5h18" />
          <path d="M8 15h2" />
          <path d="M13 15h4" />
        </svg>
      );
    case 'bots':
      return (
        <svg {...common}>
          <rect x="5" y="7" width="14" height="10" rx="3" />
          <path d="M12 4v3" />
          <circle cx="9" cy="12" r="1" />
          <circle cx="15" cy="12" r="1" />
          <path d="M8 17.5c1.2-1 6.8-1 8 0" />
        </svg>
      );
    case 'knowledge':
      return (
        <svg {...common}>
          <path d="M7 5h10" />
          <path d="M6 8h12" />
          <path d="M6 11h12" />
          <path d="M6 14h12" />
          <path d="M8 19h8" />
          <rect x="5" y="4" width="14" height="16" rx="2" />
        </svg>
      );
    case 'company':
      return (
        <svg {...common}>
          <rect x="3" y="9" width="8" height="12" rx="2" />
          <rect x="13" y="5" width="8" height="16" rx="2" />
          <path d="M6.5 13h0.01" />
          <path d="M6.5 16h0.01" />
          <path d="M17.5 9h0.01" />
          <path d="M17.5 13h0.01" />
          <path d="M17.5 17h0.01" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83l-.12.12a2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 0 1-2 2h-.17a2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0l-.12-.12a2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1-2-2v-.17a2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83l.12-.12a2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 2-2h.17a2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0l.12.12a2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8a1.65 1.65 0 0 0 1.51 1H22a2 2 0 0 1 2 2v.17a2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'integrations':
      return (
        <svg {...common}>
          <path d="M12 5V3" />
          <path d="M12 21v-2" />
          <path d="M19 12h2" />
          <path d="M3 12h2" />
          <circle cx="12" cy="12" r="3.2" />
          <path d="M8.5 8.5 6.8 6.8" />
          <path d="M17.2 17.2 15.5 15.5" />
          <path d="M15.5 8.5 17.2 6.8" />
          <path d="M6.8 17.2 8.5 15.5" />
        </svg>
      );
    case 'tools':
      return (
        <svg {...common}>
          <path d="M13 3a4 4 0 1 1 8 8l-4 4-4-4" />
          <path d="m3 13 4-4 4 4-4 4-3-3" />
          <path d="m10 10 4 4" />
        </svg>
      );
    case 'booking':
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="15" rx="3" />
          <path d="M9 3v4" />
          <path d="M15 3v4" />
          <path d="M4 10h16" />
          <rect x="8" y="13" width="3.5" height="3.5" rx="1" />
          <rect x="13.5" y="13" width="3.5" height="3.5" rx="1" />
        </svg>
      );
    case 'members':
      return (
        <svg {...common}>
          <circle cx="8" cy="7" r="2.5" />
          <circle cx="16" cy="7" r="2.5" />
          <path d="M4 16c0-2 1.5-4 4-4s4 2 4 4v2H4z" />
          <path d="M12 16c0-2 1.2-4 4-4s4 2 4 4v2h-8z" />
        </svg>
      );
    case 'clipboard-check':
      return (
        <svg {...common}>
          <path d="M9 4h6" />
          <path d="M9 4a3 3 0 0 1 6 0" />
          <rect x="5" y="5" width="14" height="16" rx="3" />
          <path d="M9 11h6" />
          <path d="M9 16l2 2 4-5" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="15" rx="3" />
          <path d="M9 3v4" />
          <path d="M15 3v4" />
          <path d="M4 10h16" />
          <path d="M8 14h2" />
          <path d="M14 14h2" />
        </svg>
      );
    case 'chat-form':
      return (
        <svg {...common}>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 9h8" />
          <path d="M8 12h6" />
          <path d="M8 15h4" />
        </svg>
      );
    case 'chat-history':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'missions':
      return (
        <svg {...common}>
          <rect x="6" y="3" width="12" height="18" rx="3" />
          <path d="M10 7h4" />
          <path d="M9.5 12h5" />
          <path d="M9.5 16h5" />
        </svg>
      );
    case 'install':
      return (
        <svg {...common}>
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <rect x="5" y="17" width="14" height="4" rx="1.5" />
        </svg>
      );
    case 'insights':
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 16l4-4 4 4 5-6" />
        </svg>
      );
    case 'agent':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
          <path d="M15 4.5c1.5 1 2.5 3 2.5 3.5" />
          <circle cx="18" cy="6" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'notifications':
      return (
        <svg {...common}>
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 6-3 8h18c0-2-3-1-3-8" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
      );
    case 'inbox':
      return (
        <svg {...common}>
          <path d="M5 4h14l2 9v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5l2-9z" />
          <path d="M3 13h5l1.5 3h5L16 13h5" />
        </svg>
      );
    case 'tasks':
      return (
        <svg {...common}>
          <rect x="5" y="4" width="14" height="16" rx="3" />
          <path d="M9 9l1.5 1.5L14 7" />
          <path d="M9 15h6" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
};

const SidebarToggleIcon = ({ collapsed = false }) => (
  <svg
    className="sidebar-toggle-icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <path d="M9 4v16" />
    {collapsed ? (
      <path d="M14 9l3 3-3 3" />
    ) : (
      <path d="M17 9l-3 3 3 3" />
    )}
  </svg>
);

// Innsikt submenu items
const INNSIKT_CHILDREN = [
  { to: '/chat-historikk', label: 'Chat-historikk', icon: 'chat-history', permission: 'canViewChatHistory' },
];

const HENVENDELSER_CHILDREN = [
  { to: '/henvendelser/innboks', label: 'Innboks', icon: 'inbox', permission: 'canViewInbox' },
  { to: '/henvendelser/oppgaver', label: 'Oppgaver', icon: 'tasks', permission: 'canViewInbox' },
  { to: '/henvendelser/kalender', label: 'Kalender', icon: 'calendar', permission: 'canViewInbox' },
];

const CONFIGURATION_CHILDREN = [
  { to: '/konfigurasjon/produkter', label: 'Produkter/Tjenester', icon: 'products', permission: 'canViewProducts' },
  { to: '/konfigurasjon/henvendelsestyper', label: 'Henvendelsestyper', icon: 'missions', permission: 'canViewInquiryTypes' },
  { to: '/konfigurasjon/skjema', label: 'Skjema', icon: 'chat-form', permission: 'canViewForms' },
];

// Navigation items with permission requirements
const NAV_ITEMS = {
  dashboard: { to: '/', label: 'Hjem', icon: 'home', permission: 'canViewDashboard' },
  agent: { to: '/agent', label: 'Agent', icon: 'agent', permission: 'canViewAgent' },
  henvendelser: { to: '/henvendelser', label: 'Henvendelser', icon: 'clipboard-check', children: HENVENDELSER_CHILDREN, permission: 'canViewInquiryOverview' },
  configuration: { to: '/konfigurasjon', label: 'Konfigurasjon', icon: 'settings', children: CONFIGURATION_CHILDREN },
  innsikt: { to: '/innsikt', label: 'Innsikt', icon: 'insights', children: INNSIKT_CHILDREN, permission: 'canViewInnsikt' },
  bots: { to: '/bots', label: 'Bots', icon: 'bots', children: [], permission: 'canViewBots' },
  company: {
  to: '/selskap',
  label: 'Selskap',
  icon: 'company',
    permission: 'canViewCompany',
  children: [
    { to: '/selskap/medlemmer', label: 'Medlemmer', icon: 'members', permission: 'canViewMembers' },
    { to: '/selskap/tilganger', label: 'Tilganger', icon: 'clipboard-check', permission: 'canViewAccess' },
    { to: '/selskap/innstillinger', label: 'Innstillinger', icon: 'settings', permission: 'canViewCompanySettings' },
    { to: '/selskap/google-calendar', label: 'Google Calendar', icon: 'calendar', permission: 'canViewCompanySettings' },
    { to: '/selskap/tilleggsverktoy', label: 'Tilleggsverktøy', icon: 'tools', permission: 'canViewAddons' },
    { to: '/abonnement', label: 'Abonnement', icon: 'billing', permission: 'canViewBilling' },
  ],
  },
};

const filterChildren = (item, permissions) => {
  if (!Array.isArray(item.children)) return item;
  return {
    ...item,
    children: item.children.filter((child) => !child.permission || hasPermission(permissions, child.permission)),
  };
};

const hasPermission = (permissions = {}, key) => {
  return Boolean(permissions?.[key]);
};

// Build navigation based on permissions
const buildNavigation = (permissions) => {
  const items = [];
  
  // Dashboard - always visible
  items.push(NAV_ITEMS.dashboard);
  
  // Agent - visible to users with agent page access.
  if (permissions.canViewAgent) {
    items.push(NAV_ITEMS.agent);
  }

  const henvendelser = filterChildren(NAV_ITEMS.henvendelser, permissions);
  if (hasPermission(permissions, NAV_ITEMS.henvendelser.permission) || henvendelser.children.length > 0) {
    items.push(henvendelser);
  }

  const configuration = filterChildren(NAV_ITEMS.configuration, permissions);
  if (configuration.children.length > 0) {
    items.push(configuration);
  }
  
  // Innsikt - visible to managers, owners
  if (hasPermission(permissions, 'canViewChatHistory')) {
    const innsikt = filterChildren(NAV_ITEMS.innsikt, permissions);
    if (innsikt.children.length > 0) items.push(innsikt);
  }
  
  // Bots - visible to managers, owners
  if (permissions.canViewBots) {
    items.push(NAV_ITEMS.bots);
  }
  
  // Company - visible to owners only
  if (permissions.canViewCompany) {
    const company = filterChildren(NAV_ITEMS.company, permissions);
    if (company.children.length > 0) items.push(company);
  }

  return items;
};

const Sidebar = ({
  isMobileOpen = false,
  isCollapsed = false,
  onClose = () => { },
  onToggleCollapse = () => { },
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    activeUser,
    clearImpersonation,
    isImpersonating,
    logout,
    selectedCompany,
    selectedCompanyId,
    companiesLoading: accountLoading,
    permissions,
  } = useAuth();
  const {
    isConnected,
    connectionState,
    agentDesiredAvailable,
    agentIsAvailable,
    agentAvailabilityLoading,
    myChats,
    setAvailability: setAgentAvailability,
  } = useDashboardRealtime();

  const displayName = user ? (user.displayName || user.login || user.email || 'Kunde') : null;
  const activeCustomerName = isImpersonating ? activeUser?.displayName : null;
  const selectedCompanyName = selectedCompany?.name || (accountLoading ? 'Laster…' : 'Ingen selskap valgt');
  
  // Build navigation based on user permissions
  const navEntries = useMemo(() => {
    return buildNavigation(permissions || {});
  }, [permissions]);
  const [inquiryUnreadCount, setInquiryUnreadCount] = useState(0);
  const [openSections, setOpenSections] = useState({});
  const unreadCountText = inquiryUnreadCount > 99 ? '99+' : String(inquiryUnreadCount);
  const resolvedCompanyId = selectedCompanyId || selectedCompany?.id || null;

  const loadInquiryUnreadCount = useCallback(async () => {
    if (!permissions?.canViewInbox || !resolvedCompanyId) {
      setInquiryUnreadCount(0);
      return;
    }
    try {
      const data = await DashboardApi.getCompanyInboxSummary(resolvedCompanyId, { compact: 1 });
      const unread = Number(data?.totals?.unread || 0);
      setInquiryUnreadCount(Number.isFinite(unread) && unread > 0 ? unread : 0);
    } catch (_error) {
      setInquiryUnreadCount(0);
    }
  }, [permissions?.canViewInbox, resolvedCompanyId]);

  useEffect(() => {
    setOpenSections({});
  }, [permissions]);

  useEffect(() => {
    // Handle special case for innsikt routes that should expand the Innsikt menu
    const innsiktPaths = ['/chat-historikk'];
    if (innsiktPaths.some((path) => location.pathname.startsWith(path))) {
      setOpenSections((prev) => ({ ...prev, '/innsikt': true }));
      return;
    }

    const activeEntry = [...navEntries]
      .sort((left, right) => right.to.length - left.to.length)
      .find((entry) => entry.to !== '/' && location.pathname.startsWith(entry.to));
    if (activeEntry && Array.isArray(activeEntry.children) && activeEntry.children.length > 0) {
      setOpenSections((prev) => ({ ...prev, [activeEntry.to]: true }));
    }
  }, [navEntries, location.pathname]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!mounted) return;
      await loadInquiryUnreadCount();
    };

    run();
    const timer = window.setInterval(run, 60000);
    const handleFocus = () => {
      run();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      mounted = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadInquiryUnreadCount]);

  const handleToggleSection = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleParentNavClick = (entry, hasChildren) => {
    if (hasChildren) {
      setOpenSections((prev) => {
        if (prev[entry.to]) {
          return prev;
        }
        return { ...prev, [entry.to]: true };
      });
    }
    onClose();
  };
  const initials = displayName
    ? displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
    : 'DU';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
    onClose();
  };

  const handleClearImpersonation = () => {
    clearImpersonation();
    navigate('/', { replace: true });
    onClose();
  };

  const sidebarClassName = `sidebar${isMobileOpen ? ' is-mobile-open' : ''}${isCollapsed ? ' collapsed' : ''}`;

  const handleSidebarToggle = () => {
    if (isMobileOpen) {
      onClose();
      return;
    }
    onToggleCollapse();
  };

  const handleAgentAvailabilitySet = async (event, nextValue) => {
    event.preventDefault();
    event.stopPropagation();
    if (agentDesiredAvailable === nextValue) return;
    try {
      await setAgentAvailability(nextValue);
    } catch (error) {
      notifyError(error.message || 'Kunne ikke oppdatere agentstatus.', { source: 'Agent' });
    }
  };

  const activeAgentChatId = location.pathname.startsWith('/agent')
    ? new URLSearchParams(location.search || '').get('chat')
    : null;

  const visibleAgentChats = Array.isArray(myChats) ? myChats.slice(0, 5) : [];
  const agentStatusClass = agentDesiredAvailable && agentIsAvailable && isConnected
    ? 'is-online'
    : connectionState === 'connecting' && agentDesiredAvailable
      ? 'is-connecting'
      : 'is-offline';


  return (
    <aside id="app-sidebar" className={sidebarClassName}>
      <div className="sidebar-header">
        <div className="sidebar-header-row">
          {user?.isAdmin ? (
            <Link
              to="/admin/companies"
              className="sidebar-admin-toggle"
              onClick={onClose}
            >
              Administrasjon
            </Link>
          ) : (
            <span className="sidebar-badge">Administrasjon</span>
          )}
          <button
            type="button"
            className="sidebar-close"
            onClick={handleSidebarToggle}
            aria-label={isCollapsed ? 'Utvid meny' : 'Skjul meny'}
          >
            <SidebarToggleIcon collapsed={isCollapsed} />
          </button>
        </div>
        <div className="sidebar-brand">
          <div className="sidebar-logo-dot" aria-hidden="true">
            <img src="/kichat-logo.png" alt="" />
          </div>
          <div className="sidebar-brand-text">
            <h1>KI Chat</h1>
            <p>Kontrollsenter</p>
          </div>
        </div>
      </div>
      <nav className="sidebar-nav">
        {isImpersonating && activeCustomerName && (
          <div className="sidebar-impersonation">
            <span>
              Administrerer: <strong>{activeCustomerName}</strong>
            </span>
            <button type="button" onClick={handleClearImpersonation}>
              Tilbakestill
            </button>
          </div>
        )}
        {navEntries.map((entry) => {
          const hasChildren = Array.isArray(entry.children) && entry.children.length > 0;
          const isAgentEntry = entry.to === '/agent';
          const shouldUseExact = entry.to === '/' || entry.to === '/bots' || entry.to === '/henvendelser';
          const isSectionOpen = hasChildren ? openSections[entry.to] === true : false;
          return (
            <React.Fragment key={entry.to}>
              <NavLink
                to={entry.to}
                end={shouldUseExact}
                className={({ isActive }) =>
                  isActive ? 'sidebar-link active' : 'sidebar-link'
                }
                title={entry.label}
                aria-label={entry.label}
                onClick={() => handleParentNavClick(entry, hasChildren)}
                aria-expanded={hasChildren ? isSectionOpen : undefined}
              >
                <span className="sidebar-icon">
                  <Icon name={entry.icon} />
                </span>
                <div className="sidebar-text">
                  <span className="sidebar-label">{entry.label}</span>
                  {isAgentEntry && (
                    <span className={`sidebar-agent-availability ${agentStatusClass}`}>
                      <span className="sidebar-agent-status-dot" aria-hidden="true" />
                      <button
                        type="button"
                        className={agentDesiredAvailable ? 'active' : ''}
                        onClick={(event) => handleAgentAvailabilitySet(event, true)}
                        disabled={agentAvailabilityLoading}
                        aria-pressed={agentDesiredAvailable}
                      >
                        Tilgjengelig
                      </button>
                      <button
                        type="button"
                        className={!agentDesiredAvailable ? 'active' : ''}
                        onClick={(event) => handleAgentAvailabilitySet(event, false)}
                        disabled={agentAvailabilityLoading}
                        aria-pressed={!agentDesiredAvailable}
                      >
                        Utilgjengelig
                      </button>
                    </span>
                  )}
                </div>
                {hasChildren && (
                  <button
                    type="button"
                    className={`sidebar-chevron${isSectionOpen ? ' open' : ''}`}
                    onClick={(event) => handleToggleSection(event, entry.to)}
                    aria-label={isSectionOpen ? 'Skjul undermeny' : 'Vis undermeny'}
                  >
                    {isSectionOpen ? '▾' : '▸'}
                  </button>
                )}
              </NavLink>
              {isAgentEntry && visibleAgentChats.length > 0 && (
                <div className="sidebar-agent-chats" aria-label="Mine aktive agentsamtaler">
                  {visibleAgentChats.map((chat) => {
                    const isActiveChat = activeAgentChatId && String(activeAgentChatId) === String(chat.chatId);
                    const preview = chat.lastMessage || 'Aktiv samtale';
                    return (
                      <Link
                        key={chat.chatId}
                        to={`/agent?chat=${encodeURIComponent(chat.chatId)}`}
                        className={`sidebar-agent-chat-link ${isActiveChat ? 'active' : ''}`}
                        title={preview}
                        onClick={onClose}
                      >
                        <span className="sidebar-agent-chat-dot" aria-hidden="true" />
                        <span className="sidebar-agent-chat-text">
                          <strong>{chat.originChannel || 'Web'}</strong>
                          <small>{preview.length > 38 ? `${preview.slice(0, 38)}...` : preview}</small>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
              {hasChildren && isSectionOpen && (
                <div className="sidebar-section-items nested">
                  {entry.children.map((child) => {
                    const childUseExact = child.to.startsWith('/bots/flows')
                      || child.to === '/henvendelser'
                      || child.to === '/henvendelser/innboks'
                      || child.to === '/henvendelser/oppgaver'
                      || child.to === '/henvendelser/kalender';
                    const showInquiryUnreadBadge = child.to === '/henvendelser/innboks' && inquiryUnreadCount > 0;
                    const isInboxChild = child.to === '/henvendelser/innboks';
                    const isInquiryDetailRoute = isInboxChild
                      && location.pathname.startsWith('/henvendelser/')
                      && !location.pathname.startsWith('/henvendelser/innboks')
                      && !location.pathname.startsWith('/henvendelser/oppgaver')
                      && !location.pathname.startsWith('/henvendelser/kalender')
                      && !location.pathname.startsWith('/henvendelser/typer')
                      && !location.pathname.startsWith('/henvendelser/skjema');
                    return (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={childUseExact}
                        className={({ isActive }) =>
                          (isActive || isInquiryDetailRoute)
                            ? 'sidebar-link child active'
                            : 'sidebar-link child'
                        }
                        title={child.label}
                        aria-label={child.label}
                        onClick={onClose}
                      >
                        <span className="sidebar-icon">
                          <Icon name={child.icon} />
                        </span>
                        <div className="sidebar-text">
                          <span className="sidebar-label">{child.label}</span>
                        </div>
                        {showInquiryUnreadBadge && (
                          <span className="sidebar-link-badge">{unreadCountText}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        {user && (
          <>
            <div className="sidebar-company">
              <div className="sidebar-company-meta">
                <span className="sidebar-company-label">Selskap</span>
                <strong className="sidebar-company-name">{selectedCompanyName}</strong>
                {selectedCompany?.planTier && (
                  <span className="sidebar-company-plan">
                    Plan: {selectedCompany.planTier}
                  </span>
                )}
              </div>
              <Link
                to="/companies"
                className="sidebar-company-switch"
                onClick={onClose}
              >
                Administrer
              </Link>
            </div>
            <Link to="/konto/profil" className="sidebar-user" onClick={onClose}>
              <div className="sidebar-user-avatar" aria-hidden="true">
                {initials || 'DU'}
              </div>
              <div className="sidebar-user-info">
                <p className="sidebar-user-name">{displayName}</p>
                {user.email && <p className="sidebar-user-email">{user.email}</p>}
              </div>
            </Link>
            <button type="button" className="sidebar-logout" onClick={handleLogout}>
              Logg ut
            </button>
            {isImpersonating && (
              <button type="button" className="sidebar-logout secondary" onClick={handleClearImpersonation}>
                Avslutt kundemodus
              </button>
            )}
          </>
        )}
        <NavLink to="/endringslogg" className="sidebar-help" onClick={onClose}>
          Endringslogg
        </NavLink>
      </div>
    </aside>
  );
};

export default Sidebar;
